import { useCallback, useMemo, useState } from "react";
import { Button, Checkbox, ListItemText, MenuItem, Select, TextField } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { catalogQuietButtonSx } from "../Products/catalogGridStyles.js";

const OPERATORS = {
    text: [
        { id: "contains", label: "contains" },
        { id: "notContains", label: "does not contain" },
        { id: "is", label: "is" },
        { id: "isNot", label: "is not" },
        { id: "empty", label: "is empty" },
        { id: "notEmpty", label: "is not empty" },
    ],
    select: [
        { id: "is", label: "is" },
        { id: "isNot", label: "is not" },
        { id: "isAnyOf", label: "is any of" },
        { id: "empty", label: "is empty" },
        { id: "notEmpty", label: "is not empty" },
    ],
    number: [
        { id: "eq", label: "=" },
        { id: "neq", label: "≠" },
        { id: "gt", label: ">" },
        { id: "lt", label: "<" },
        { id: "gte", label: "≥" },
        { id: "lte", label: "≤" },
        { id: "empty", label: "is empty" },
        { id: "notEmpty", label: "is not empty" },
    ],
    date: [
        { id: "is", label: "is" },
        { id: "before", label: "is before" },
        { id: "after", label: "is after" },
        { id: "empty", label: "is empty" },
        { id: "notEmpty", label: "is not empty" },
    ],
};

let nextId = 1;

function fieldById(fields, fieldId) {
    return fields.find((field) => field.id === fieldId) || fields[0];
}

function operatorsFor(type) {
    return OPERATORS[type] || OPERATORS.text;
}

function newCondition(fields) {
    const field = fields[0];
    return {
        id: nextId++,
        field: field.id,
        operator: operatorsFor(field.type)[0].id,
        value: "",
    };
}

function normalizeMultiValue(value) {
    if (Array.isArray(value)) {
        return value.map((entry) => String(entry ?? "").trim()).filter(Boolean);
    }
    if (value == null) return [];
    const text = String(value).trim();
    return text ? [text] : [];
}

function isBlank(value) {
    if (Array.isArray(value)) return normalizeMultiValue(value).length === 0;
    return value == null || String(value).trim() === "";
}

function isActiveCondition(condition) {
    if (!condition?.field || !condition?.operator) return false;
    if (condition.operator === "empty" || condition.operator === "notEmpty") return true;
    return !isBlank(condition.value);
}

function valueForOperator(operator, currentValue) {
    if (operator === "isAnyOf") return normalizeMultiValue(currentValue);
    if (Array.isArray(currentValue)) return normalizeMultiValue(currentValue)[0] || "";
    return currentValue ?? "";
}

function dayKey(value) {
    if (value == null || value === "") return "";
    if (typeof value === "string") {
        const iso = value.trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso.slice(0, 10);
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    // Prefer calendar day in local time for locale display strings (e.g. 10/15/2026).
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function readValue(record, field) {
    if (typeof field.getValue === "function") return field.getValue(record);
    return record?.[field.id];
}

function matches(record, field, condition) {
    const raw = readValue(record, field);
    const op = condition.operator;
    if (op === "empty") return isBlank(raw);
    if (op === "notEmpty") return !isBlank(raw);
    if (isBlank(condition.value)) return true;

    if (field.type === "number") {
        const left = Number(raw);
        const right = Number(condition.value);
        if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
        if (op === "eq") return left === right;
        if (op === "neq") return left !== right;
        if (op === "gt") return left > right;
        if (op === "lt") return left < right;
        if (op === "gte") return left >= right;
        if (op === "lte") return left <= right;
        return false;
    }

    if (field.type === "date") {
        const left = dayKey(raw);
        const right = String(condition.value).slice(0, 10);
        if (!left || !right) return false;
        if (op === "is") return left === right;
        if (op === "before") return left < right;
        if (op === "after") return left > right;
        return false;
    }

    const left = String(raw ?? "").toLowerCase();
    if (op === "isAnyOf") {
        const rights = normalizeMultiValue(condition.value).map((entry) => entry.toLowerCase());
        return rights.includes(left);
    }
    const right = String(condition.value).toLowerCase();
    if (op === "contains") return left.includes(right);
    if (op === "notContains") return !left.includes(right);
    if (op === "is") return left === right;
    if (op === "isNot") return left !== right;
    return true;
}

export function applyGridFilter(records, fields, conjunction, conditions) {
    const active = (conditions || []).filter(isActiveCondition);
    if (!active.length || !fields?.length) return records || [];
    const checks = active.map((condition) => ({
        condition,
        field: fieldById(fields, condition.field),
    }));
    const list = records || [];
    if (conjunction === "or") {
        return list.filter((record) =>
            checks.some(({ condition, field }) => matches(record, field, condition))
        );
    }
    return list.filter((record) =>
        checks.every(({ condition, field }) => matches(record, field, condition))
    );
}

/** Parse persisted filter JSON (URL / storage) into hook initial state. */
export function parsePersistedGridFilters(raw) {
    if (raw == null || raw === "") return null;
    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!parsed || typeof parsed !== "object") return null;
        const conjunction = parsed.conjunction === "or" ? "or" : "and";
        const conditions = (Array.isArray(parsed.conditions) ? parsed.conditions : [])
            .map((condition) => ({
                id: nextId++,
                field: String(condition?.field || "").trim(),
                operator: String(condition?.operator || "contains").trim() || "contains",
                value: condition?.value ?? "",
            }))
            .filter((condition) => condition.field);
        if (!conditions.length && conjunction === "and" && !parsed.open) return null;
        return {
            conjunction,
            conditions,
            open: Boolean(parsed.open) || conditions.some(isActiveCondition),
        };
    } catch {
        return null;
    }
}

/** Compact JSON for URL / storage (no React row ids). */
export function serializeGridFilters({ conjunction = "and", conditions = [], open = false } = {}) {
    const cleaned = (conditions || [])
        .map((condition) => ({
            field: String(condition?.field || "").trim(),
            operator: String(condition?.operator || "").trim(),
            value: condition?.value ?? "",
        }))
        .filter((condition) => condition.field && condition.operator);
    if (!cleaned.length) return "";
    return JSON.stringify({
        conjunction: conjunction === "or" ? "or" : "and",
        open: Boolean(open),
        conditions: cleaned,
    });
}

/**
 * @param {object[]} fields
 * @param {{ initial?: { conjunction?: string, conditions?: object[], open?: boolean } | null }} [options]
 */
export function useGridFilter(fields, options = {}) {
    const initial = options?.initial || null;
    const [conjunction, setConjunction] = useState(() =>
        initial?.conjunction === "or" ? "or" : "and"
    );
    const [conditions, setConditions] = useState(() =>
        Array.isArray(initial?.conditions) ? initial.conditions : []
    );
    const [open, setOpen] = useState(() =>
        Boolean(initial?.open) ||
        (Array.isArray(initial?.conditions) &&
            initial.conditions.some(isActiveCondition))
    );
    const activeCount = conditions.filter(isActiveCondition).length;

    const apply = useCallback(
        (records) => applyGridFilter(records, fields, conjunction, conditions),
        [fields, conjunction, conditions]
    );

    const activeConditions = useMemo(
        () => conditions.filter(isActiveCondition),
        [conditions]
    );

    return {
        apply,
        conditions,
        activeConditions,
        conjunction,
        open,
        activeCount,
        filterActive: activeCount > 0,
        barProps: {
            fields,
            open,
            conjunction,
            conditions,
            onToggle: () => setOpen((value) => !value),
            onConjunction: setConjunction,
            onAdd: () => {
                setOpen(true);
                setConditions((prev) => [...prev, newCondition(fields)]);
            },
            onRemove: (id) => setConditions((prev) => prev.filter((condition) => condition.id !== id)),
            onChange: (id, patch) =>
                setConditions((prev) =>
                    prev.map((condition) => (condition.id === id ? { ...condition, ...patch } : condition))
                ),
        },
    };
}

const controlSx = {
    height: 34,
    fontSize: 13,
    backgroundColor: "#fff",
    borderRadius: "8px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
    "& .MuiSelect-select": { py: "6px", fontSize: 13 },
};

const multiSelectSx = {
    ...controlSx,
    height: "auto",
    minHeight: 34,
    "& .MuiSelect-select": { py: "6px", fontSize: 13, minHeight: "auto" },
};

export default function GridFilterBar({
    fields,
    open,
    conjunction,
    conditions,
    onToggle,
    onConjunction,
    onAdd,
    onRemove,
    onChange,
}) {
    return (
        <div style={{ flexShrink: 0, borderBottom: "1px solid #eee", background: "#fff" }}>
            <div className="catalog-toolbar" style={{ borderBottom: "none" }}>
                <Button
                    size="small"
                    startIcon={<FilterListOutlinedIcon fontSize="small" />}
                    endIcon={
                        <KeyboardArrowDownRoundedIcon
                            fontSize="small"
                            sx={{
                                transition: "transform 0.15s ease",
                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                        />
                    }
                    onClick={onToggle}
                    aria-expanded={open}
                    sx={catalogQuietButtonSx}
                >
                    {open ? "Hide filters" : "Filter"}
                    {conditions.length ? ` · ${conditions.length}` : ""}
                </Button>
                {conditions.length ? (
                    <span className="catalog-selection-hint">
                        {conjunction === "or" ? "Any condition can match" : "Every condition must match"}
                    </span>
                ) : (
                    <span className="catalog-selection-hint">No filters</span>
                )}
            </div>
            {open ? (
                <div style={{ padding: "0 16px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, color: "#5c6570" }}>Match</span>
                        <Select
                            size="small"
                            value={conjunction}
                            onChange={(e) => onConjunction(e.target.value)}
                            sx={{ ...controlSx, minWidth: 88 }}
                        >
                            <MenuItem value="and">all</MenuItem>
                            <MenuItem value="or">any</MenuItem>
                        </Select>
                        <Button
                            size="small"
                            startIcon={<AddOutlinedIcon fontSize="small" />}
                            onClick={onAdd}
                            sx={catalogQuietButtonSx}
                        >
                            Add condition
                        </Button>
                    </div>
                    {conditions.map((condition, index) => {
                        const meta = fieldById(fields, condition.field);
                        const operators = operatorsFor(meta.type);
                        const hidesValue = condition.operator === "empty" || condition.operator === "notEmpty";
                        return (
                            <div key={condition.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ width: 36, fontSize: 12, fontWeight: 700, color: "#94a3b8" }}>
                                    {index === 0 ? "Where" : conjunction === "or" ? "or" : "and"}
                                </span>
                                <Select
                                    size="small"
                                    value={condition.field}
                                    onChange={(e) => {
                                        const nextField = fieldById(fields, e.target.value);
                                        onChange(condition.id, {
                                            field: nextField.id,
                                            operator: operatorsFor(nextField.type)[0].id,
                                            value: "",
                                        });
                                    }}
                                    sx={{ ...controlSx, minWidth: 180 }}
                                >
                                    {fields.map((field) => (
                                        <MenuItem key={field.id} value={field.id}>
                                            {field.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Select
                                    size="small"
                                    value={condition.operator}
                                    onChange={(e) => {
                                        const nextOperator = e.target.value;
                                        onChange(condition.id, {
                                            operator: nextOperator,
                                            value: valueForOperator(nextOperator, condition.value),
                                        });
                                    }}
                                    sx={{ ...controlSx, minWidth: 150 }}
                                >
                                    {operators.map((operator) => (
                                        <MenuItem key={operator.id} value={operator.id}>
                                            {operator.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {hidesValue ? (
                                    <span style={{ flex: 1 }} />
                                ) : meta.type === "select" && condition.operator === "isAnyOf" ? (
                                    <Select
                                        size="small"
                                        multiple
                                        displayEmpty
                                        value={normalizeMultiValue(condition.value)}
                                        onChange={(e) =>
                                            onChange(condition.id, {
                                                value: normalizeMultiValue(e.target.value),
                                            })
                                        }
                                        renderValue={(selected) =>
                                            selected.length ? selected.join(", ") : "Select…"
                                        }
                                        sx={{ ...multiSelectSx, minWidth: 160, flex: 1 }}
                                    >
                                        {(meta.options || []).map((option) => {
                                            const selected = normalizeMultiValue(condition.value);
                                            return (
                                                <MenuItem key={option} value={option}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={selected.includes(option)}
                                                    />
                                                    <ListItemText primary={option} />
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                ) : meta.type === "select" ? (
                                    <Select
                                        size="small"
                                        displayEmpty
                                        value={Array.isArray(condition.value) ? "" : condition.value}
                                        onChange={(e) => onChange(condition.id, { value: e.target.value })}
                                        sx={{ ...controlSx, minWidth: 160, flex: 1 }}
                                    >
                                        <MenuItem value="">Select…</MenuItem>
                                        {(meta.options || []).map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                ) : (
                                    <TextField
                                        size="small"
                                        type={meta.type === "date" ? "date" : meta.type === "number" ? "number" : "text"}
                                        value={condition.value}
                                        placeholder="Value"
                                        onChange={(e) => onChange(condition.id, { value: e.target.value })}
                                        sx={{ flex: 1, maxWidth: 280, "& .MuiInputBase-input": { py: "6px", fontSize: 13 } }}
                                    />
                                )}
                                <Button
                                    size="small"
                                    onClick={() => onRemove(condition.id)}
                                    sx={{ minWidth: 34, px: 0.5, color: "#64748b" }}
                                    aria-label="Remove condition"
                                >
                                    <CloseOutlinedIcon fontSize="small" />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
